import { LogsComponent } from './logs/list/logs.component';
import { FlightDetailsComponent } from './flights/details/flight-details.component';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { AppComponent } from './app.component';
import { NavbarComponent } from './navbar/navbar.component';
import { LoginComponent } from './auth/login/login.component';
import { AuthService } from './auth/auth.service';
import { IsLoggedInService } from './auth/isLoggedIn.service';
import { MainComponent } from './main/main.component';
import { NotLoggedInService } from './auth/notLoggedIn.service';
import { LogoutComponent } from './auth/login/logout.component';
import { LocationComponent } from './locations/single/location.component';
import { LocationsComponent } from './locations/list/locations.component';
import { LocationService } from './locations/location.service';
import { LocationEditActivateGuard, LocationEditDeactivateGuard } from './locations/location-guard.service';
import { BranchesComponent } from './branches/list/branches.component';
import { BranchComponent } from './branches/single/branch.component';
import { BranchEditActivateGuard, BranchEditDeactivateGuard } from './branches/branch-guard.service';
import { BranchService } from './branches/branch.service';
import { AircraftService } from './aircrafts/aircraft.service';
import { AircraftsComponent } from './aircrafts/list/aircrafts.component';
import { AircraftComponent } from './aircrafts/single/aircraft.component';
import { AircraftEditActivateGuard, AircraftEditDeactivateGuard } from './aircrafts/aircraft-guard.service';
import { FlightGroupsComponent } from './flight_group/list/flight-groups.component';
import { FlightGroupComponent } from './flight_group/single/flight-group.component';
import { FlightGroupService } from './flight_group/flight-group.service';
import { FlightGroupActivateGuard, FlightGroupDeactivateGuard } from './flight_group/flight-group-guard.service';
import { FlightsComponent } from './flights/list/flights.component';
import { FlightService } from './flights/flight.service';
import { FlightComponent } from './flights/single/flight.component';
import { AngularFontAwesomeModule } from 'angular-font-awesome';
import { CustomLogger } from './shared/custom-logger';
import { Helper } from './shared/helper';
import { NgxPaginationModule } from 'ngx-pagination';
import { UserComponent } from './users/single/user.component';
import { UsersComponent } from './users/list/users.component';
import { UserService } from './users/user.service';
import { UserEditActivateGuard, UserEditDeactivateGuard } from './users/user-guard.service';
import { JwtModule } from '@auth0/angular-jwt';
import { TokenInterceptor, JwtInterceptor } from './auth/token.interceptor';
import { DatePipe } from '@angular/common';

import { BulkEditComponent } from './flights/bulk-edit/bulk-edit.component';

import { TicketsModule } from './tickets/tickets.module';
import { SharedModule } from './shared/shared.module';

import { RoleGuardService as RoleGuard } from './auth/role-guard.service';
import { LogService } from './logs/list/log.service';
import { LogDetailsComponent } from './logs/log-details/log-details.component';

import { Nl2BrPipeModule } from "nl2br-pipe";

@NgModule({
  declarations: [
    AppComponent,
    NavbarComponent,
    LoginComponent,
    LogoutComponent,
    MainComponent,
    LocationComponent,
    LocationsComponent,
    BranchComponent,
    BranchesComponent,
    AircraftsComponent,
    AircraftComponent,
    FlightGroupsComponent,
    FlightGroupComponent,
    FlightComponent,
    FlightsComponent,
    UserComponent,
    UsersComponent,
    BulkEditComponent,
    FlightDetailsComponent,
    LogsComponent,
    LogDetailsComponent
  ],
  entryComponents: [
    FlightComponent,
    LocationComponent,
    BranchComponent,
    AircraftComponent,
    UserComponent,
    FlightGroupComponent,
    BulkEditComponent,
    FlightDetailsComponent,
    LogDetailsComponent
  ],
  imports: [
    SharedModule,
    JwtModule.forRoot({
      config: {
        tokenGetter: () => {
          return localStorage.getItem('access_token');
        },
        whitelistedDomains: ['http://vsky-api.omedialab.com'],
        blacklistedRoutes: ['']
      }
    }),
    RouterModule.forRoot([
      { path: '', component: MainComponent, canActivate: [IsLoggedInService] },
      /* Start Locations */
      {
        path: 'locations',
        component: LocationsComponent,
        canActivate: [IsLoggedInService]
      },
      /* End Locations */
      /* Start Branches */
      {
        path: 'branches',
        component: BranchesComponent,
        canActivate: [IsLoggedInService]
      },
      /* End Branches */
      /* Start Aircraft */
      {
        path: 'aircrafts',
        component: AircraftsComponent,
        canActivate: [IsLoggedInService]
      },
      /* End Aircraft */
      /* Start Flight Group */
      {
        path: 'flight-groups',
        component: FlightGroupsComponent,
        canActivate: [IsLoggedInService]
      },
      /* End Flight Group */
      /* Start Flight */
      {
        path: 'flights',
        component: FlightsComponent,
        canActivate: [IsLoggedInService]
      },
      /* End Flight */
      /* Start Users */
      {
        path: 'users',
        component: UsersComponent,
        canActivate: [IsLoggedInService, RoleGuard],
        data: {
          expectedRole: ['ROLE_ADMIN', 'ROLE_ROOT_ADMIN']
        }
      },
      /* End Users */
      /* Start Log */
      {
        path: 'log',
        component: LogsComponent,
        canActivate: [IsLoggedInService, RoleGuard],
        data: {
          expectedRole: ['ROLE_ADMIN', 'ROLE_ROOT_ADMIN']
        }
      },
      /* End Users */
      {
        path: 'login',
        component: LoginComponent,
        canActivate: [NotLoggedInService]
      },
      {
        path: 'logout',
        component: LogoutComponent,
        canActivate: [IsLoggedInService]
      }
    ]),
    TicketsModule
  ],
  providers: [
    AuthService,
    IsLoggedInService,
    NotLoggedInService,
    LocationService,
    LocationEditActivateGuard,
    LocationEditDeactivateGuard,
    BranchService,
    BranchEditActivateGuard,
    BranchEditDeactivateGuard,
    AircraftService,
    AircraftEditActivateGuard,
    AircraftEditDeactivateGuard,
    FlightGroupService,
    FlightGroupActivateGuard,
    FlightGroupDeactivateGuard,
    FlightService,
    CustomLogger,
    UserService,
    UserEditActivateGuard,
    UserEditDeactivateGuard,
    Helper,
    DatePipe,
    RoleGuard,
    LogService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}



// WEBPACK FOOTER //
// ./src/app/app.module.ts